import { createTheme, alpha } from '@mui/material/styles';
import type { PaletteMode } from '@mui/material';

const NAVY = '#0B1929';
const BLUE = '#2563EB';
const BLUE_LIGHT = '#EFF6FF';
const CYAN = '#0891B2';
const BG_LIGHT = '#F8FAFC';
const BORDER_LIGHT = '#E2E8F0';

// Dark-mode surface colours that echo the navy brand
const BG_DARK = '#0B1929';
const PAPER_DARK = '#0F2233';
const BORDER_DARK = 'rgba(255,255,255,0.10)';

export function createAppTheme(mode: PaletteMode) {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: BLUE,
        light: '#3B82F6',
        dark: '#1D4ED8',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: CYAN,
        contrastText: '#FFFFFF',
      },
      background: {
        default: isDark ? BG_DARK : BG_LIGHT,
        paper: isDark ? PAPER_DARK : '#FFFFFF',
      },
      text: {
        primary: isDark ? '#E2E8F0' : '#0F172A',
        secondary: isDark ? '#94A3B8' : '#64748B',
      },
      divider: isDark ? BORDER_DARK : BORDER_LIGHT,
      success: {
        main: '#059669',
        light: isDark ? '#064E3B' : '#D1FAE5',
        contrastText: '#FFFFFF',
      },
      warning: {
        main: '#D97706',
        light: isDark ? '#451A03' : '#FEF3C7',
        contrastText: '#FFFFFF',
      },
      error: {
        main: '#DC2626',
        light: isDark ? '#450A0A' : '#FEE2E2',
        contrastText: '#FFFFFF',
      },
    },

    typography: {
      fontFamily: '"Plus Jakarta Sans", "Helvetica Neue", Arial, sans-serif',
      h1: {
        fontFamily: '"Fraunces", Georgia, serif',
        fontWeight: 300,
        letterSpacing: '-0.03em',
      },
      h2: {
        fontFamily: '"Fraunces", Georgia, serif',
        fontWeight: 400,
        letterSpacing: '-0.02em',
      },
      h3: {
        fontFamily: '"Fraunces", Georgia, serif',
        fontWeight: 400,
        letterSpacing: '-0.015em',
      },
      h4: {
        fontFamily: '"Fraunces", Georgia, serif',
        fontWeight: 400,
        letterSpacing: '-0.01em',
      },
      h5: {
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        fontWeight: 600,
        letterSpacing: '-0.005em',
      },
      h6: {
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        fontWeight: 600,
      },
      subtitle1: { fontWeight: 500 },
      subtitle2: { fontWeight: 600, letterSpacing: '0.02em', fontSize: '0.8rem' },
      button: {
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: '0.01em',
      },
      overline: {
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        fontWeight: 700,
        letterSpacing: '0.1em',
        fontSize: '0.7rem',
      },
    },

    shape: { borderRadius: 10 },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isDark ? BG_DARK : BG_LIGHT,
          },
          '.mono': { fontFamily: '"JetBrains Mono", monospace' },
        },
      },

      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: NAVY,
            borderBottom: `1px solid rgba(255,255,255,0.06)`,
          },
        },
      },

      MuiToolbar: {
        styleOverrides: {
          root: { minHeight: 56, '@media (min-width: 600px)': { minHeight: 56 } },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '9px 20px',
            fontSize: '0.875rem',
          },
          containedPrimary: {
            background: `linear-gradient(135deg, ${BLUE} 0%, #1D4ED8 100%)`,
            '&:hover': {
              background: `linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)`,
              transform: 'translateY(-1px)',
              boxShadow: `0 4px 16px ${alpha(BLUE, 0.35)}`,
            },
            transition: 'all 0.15s ease',
          },
          outlinedPrimary: {
            borderColor: isDark ? BORDER_DARK : BORDER_LIGHT,
            color: isDark ? '#E2E8F0' : '#0F172A',
            '&:hover': {
              borderColor: BLUE,
              backgroundColor: isDark ? alpha(BLUE, 0.12) : BLUE_LIGHT,
            },
          },
        },
      },

      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            border: `1px solid ${isDark ? BORDER_DARK : BORDER_LIGHT}`,
            borderRadius: 12,
            transition: 'box-shadow 0.15s ease, transform 0.15s ease',
            '&:hover': {
              boxShadow: `0 8px 24px ${alpha('#000', isDark ? 0.3 : 0.08)}`,
              transform: 'translateY(-1px)',
            },
          },
        },
      },

      MuiCardContent: {
        styleOverrides: {
          root: { padding: 20, '&:last-child': { paddingBottom: 20 } },
        },
      },

      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${isDark ? BORDER_DARK : BORDER_LIGHT}`,
          },
          elevation0: { boxShadow: 'none' },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 600,
            fontSize: '0.72rem',
            height: 24,
            letterSpacing: '0.02em',
          },
        },
      },

      MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'small' },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              backgroundColor: isDark ? alpha('#FFFFFF', 0.04) : '#FFFFFF',
              '& fieldset': { borderColor: isDark ? BORDER_DARK : BORDER_LIGHT },
              '&:hover fieldset': { borderColor: isDark ? '#64748B' : '#94A3B8' },
              '&.Mui-focused fieldset': { borderColor: BLUE, borderWidth: 2 },
            },
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            '& fieldset': { borderColor: isDark ? BORDER_DARK : BORDER_LIGHT },
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
            border: 'none',
          },
        },
      },

      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: '"Plus Jakarta Sans", sans-serif',
            fontWeight: 600,
            fontSize: '1.1rem',
            padding: '20px 24px 12px',
          },
        },
      },

      MuiDialogContent: {
        styleOverrides: { root: { padding: '8px 24px 16px' } },
      },

      MuiDialogActions: {
        styleOverrides: { root: { padding: '8px 24px 20px', gap: 8 } },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            '&:hover': { backgroundColor: isDark ? alpha(BLUE, 0.12) : BLUE_LIGHT },
          },
        },
      },

      MuiDivider: {
        styleOverrides: { root: { borderColor: isDark ? BORDER_DARK : BORDER_LIGHT } },
      },

      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 8, border: '1px solid' },
          standardError: { borderColor: isDark ? '#7F1D1D' : '#FECACA' },
          standardSuccess: { borderColor: isDark ? '#064E3B' : '#A7F3D0' },
          standardWarning: { borderColor: isDark ? '#78350F' : '#FDE68A' },
          standardInfo: { borderColor: isDark ? '#1E3A5F' : '#BFDBFE' },
        },
      },

      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: isDark ? PAPER_DARK : BG_LIGHT,
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: isDark ? '#94A3B8' : '#64748B',
            },
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: isDark ? BORDER_DARK : BORDER_LIGHT, fontSize: '0.875rem' },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            minHeight: 44,
          },
        },
      },
    },
  });
}

// Legacy default export: light theme (kept for any residual imports during migration)
const theme = createAppTheme('light');
export default theme;
